import { AlertTriangle, Clock, Eye, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// ContextBanner
//
// Dismissible banner for: archived entity,
// expired token, read-only mode.
// Design: §9.1 Alert Banner
// ─────────────────────────────────────────────

type BannerVariant = "archived" | "expired" | "readonly" | "warning" | "info";

interface ContextBannerProps {
  variant?: BannerVariant;
  title?: string;
  message: string;
  dismissible?: boolean;
  className?: string;
}

const VARIANT_CONFIG: Record<
  BannerVariant,
  {
    icon: React.ComponentType<{ className?: string }>;
    borderColor: string;
    iconClass: string;
  }
> = {
  archived: {
    icon: AlertTriangle,
    borderColor: "border-l-text-muted",
    iconClass: "text-text-muted",
  },
  expired: {
    icon: Clock,
    borderColor: "border-l-danger",
    iconClass: "text-danger",
  },
  readonly: {
    icon: Eye,
    borderColor: "border-l-accent",
    iconClass: "text-accent",
  },
  warning: {
    icon: AlertTriangle,
    borderColor: "border-l-warning",
    iconClass: "text-warning",
  },
  info: {
    icon: AlertTriangle,
    borderColor: "border-l-info",
    iconClass: "text-info",
  },
};

export function ContextBanner({
  variant = "warning",
  title,
  message,
  dismissible = true,
  className,
}: ContextBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const config = VARIANT_CONFIG[variant];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "flex items-start gap-inner rounded-sm border-l-[3px] bg-bg-alert px-4 py-3",
        config.borderColor,
        className
      )}
    >
      <Icon className={cn("mt-0.5 h-4 w-4 flex-shrink-0", config.iconClass)} />

      <div className="flex-1 text-body">
        {title && (
          <span className="font-medium text-text-primary">{title} </span>
        )}
        <span className="text-text-secondary">{message}</span>
      </div>

      {dismissible && (
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 text-text-muted transition-colors hover:text-text-secondary"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
